/**
 * Slot math for room bookings: the 30 minute lock keys and slot doc ids.
 *
 * Split out of lib/reservations.ts (speed plan 2026-10-19, P2) so the class
 * time code (lib/classSeries.ts), which is on the startup path, can use the
 * SAME functions the booking transaction uses without pulling the whole
 * booking library into the first screen. lib/reservations.ts re-exports all
 * of these unchanged, so nothing else had to move.
 */
import { localDateKey as localDateKeyImpl } from './availability';

export const SLOT_MINUTES = 30;

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** Slot key for a JS Date snapped DOWN to the nearest 30-min boundary. */
export function slotKey(d: Date): string {
  const minutes = d.getMinutes() < 30 ? 0 : 30;
  return `${localDateKeyImpl(d)}_${pad2(d.getHours())}${pad2(minutes)}`;
}

/**
 * Every 30-min slot key the half-open range [start, end) touches.
 *
 * Walks from the half-hour the booking STARTS inside, not from the raw start,
 * so a booking that does not begin on the half hour (the studio really does
 * run a 4:15 class) still locks every slot it overlaps. Starting at the raw
 * time would step 4:45 -> 5:15 and skip the 5:00 slot entirely, leaving a gap
 * another booking could slip into. For an already-aligned booking this is
 * identical to the old behavior.
 */
export function slotKeysFor(start: Date, end: Date): string[] {
  const keys: string[] = [];
  const cur = snapDown(start);
  while (cur < end) {
    keys.push(slotKey(cur));
    cur.setMinutes(cur.getMinutes() + SLOT_MINUTES);
  }
  return keys;
}

/** Composite slot doc id. */
export function slotDocId(roomId: string, key: string): string {
  return `${roomId}_${key}`;
}

/**
 * Snap a Date DOWN to the nearest 30-min boundary, zeroing seconds +
 * millis. Useful for composer normalisation so a user picking 09:14
 * actually books 09:00.
 */
export function snapDown(d: Date): Date {
  const copy = new Date(d);
  copy.setMinutes(copy.getMinutes() < 30 ? 0 : 30, 0, 0);
  return copy;
}
